import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
const ClearIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M9 9l6 6M15 9l-6 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

interface SearchHeaderProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSubmitSearch?: () => void;
  alwaysVisible?: boolean;
  syncToSelector?: string;
}




export default function SearchHeader({
  searchTerm,
  onSearchChange,
  onSubmitSearch,
  alwaysVisible = true,
  syncToSelector = ".homeLandingRoot",
}: SearchHeaderProps) {
  const [isVisible, setIsVisible] = useState(false);

  const headerRef = useRef<HTMLElement>(null);
  const hasActiveSearch = searchTerm.trim().length > 0;
  const shouldShowHeader = alwaysVisible || isVisible || hasActiveSearch;
  

function titleFromPath(pathname: string): string {
  if (pathname === "/home" || pathname === "/home/home") return "Home";
  if (pathname === "/browse" || pathname.startsWith("/home/browse") || pathname.startsWith("/browse")) return "Browse all";
  if (pathname === "/watchlist" || pathname.startsWith("/home/watchlist") || pathname.startsWith("/watchlist")) return "Watchlist";
  if (pathname.startsWith("/home/category") || pathname.startsWith("/browse/category")) return "Category";
  if (pathname.startsWith("/home/profile")) return "Profile";
  if (pathname.startsWith("/home/order_history")) return "Order history";
  if (pathname.startsWith("/home/pair_headset")) return "Pair headset";
  return "Home";
}

const { pathname } = useLocation();
const title = titleFromPath(pathname);
  useEffect(() => {
    const rootEl = document.querySelector<HTMLElement>(syncToSelector);
    if (!rootEl) return;

    const apply = () => {
      if (!headerRef.current) return;
      headerRef.current.style.width = `${rootEl.offsetWidth}px`;
      headerRef.current.style.left = `${rootEl.getBoundingClientRect().left}px`;
    };

    apply();

    const resizeObserver = new ResizeObserver(() => apply());
    resizeObserver.observe(rootEl);

    window.addEventListener("resize", apply);
    window.addEventListener("scroll", apply, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", apply);
      window.removeEventListener("scroll", apply);
    };
  }, [syncToSelector]);

  useEffect(() => {
    const heroEl = document.querySelector<HTMLElement>(".homeLandingHero");
    if (!heroEl) {
      setIsVisible(window.scrollY > 64);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setIsVisible(entry.intersectionRatio <= 0.01);
      },
      { root: null, threshold: [0, 0.01, 0.1, 0.5, 1] }
    );

    observer.observe(heroEl);
    return () => observer.disconnect();
  }, []);

  return (
    <header
      ref={headerRef}
      className={
        shouldShowHeader
          ? "homeLandingSearchHeader homeLandingSearchHeader--visible"
          : "homeLandingSearchHeader"
      }
      aria-hidden={!shouldShowHeader}
    >
      <div className="homeLandingSearchHeaderLeft">
         <h1 className="homeLandingSearchHeaderTitle">{title}</h1>
      </div>
      <div className="homeLandingSearchBar" role="search">
        <img
          className="homeLandingSearchIcon"
          src="/images/icons/Mic.png"
          alt="Search"
        />
        <input
        className="homeLandingSearchInput"
        type="text"
        placeholder="Search"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSubmitSearch?.();
          }
        }}
      />
        {hasActiveSearch && (
          <button
            type="button"
            className="homeLandingSearchClearButton"
            onClick={() => onSearchChange("")}
            aria-label="Clear search"
          >
            <ClearIcon />
          </button>
        )}
      </div>
    </header>
  );
}